import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { ThirdPartyContactDef } from '@shared/types';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { productTypeFormStore } from '../store';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/third-party-contact-type',
)({
    staticData: {
        presentation: 'overlay',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ThirdPartyContactTypeSearch />
        </PageController>
    );
}

const columnHelper = createColumnHelper<ThirdPartyContactDef>();

function ThirdPartyContactTypeSearch() {
    const orgId = useOrgId();
    const { productTypeId } = Route.useParams();
    const formData = productTypeFormStore.use.formData();
    const isLoading = productTypeFormStore.use.isLoading();
    const save = productTypeFormStore.use.save();
    const navigate = useNavigate();

    const tableProps = usePrismaTable({
        data: formData.thirdPartyContactTypes,
        total: formData.thirdPartyContactTypes?.length,
        loading: isLoading,
        rowKey: 'id',
        pagination: {
            position: 'bottom',
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
            }),
            columnHelper.accessor('allowAsMainContact', {
                id: 'allowAsMainContact',
                header: 'Allow as main contact',
                cell: ({ row }) =>
                    row.allowAsMainContact ? (
                        <Tag variant="outlined" color="green">
                            Yes
                        </Tag>
                    ) : (
                        <Tag variant="outlined" color="red">
                            No
                        </Tag>
                    ),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.Delete
                        onConfirm={async (onSuccess) => {
                            await save({
                                thirdPartyContactTypes: formData.thirdPartyContactTypes?.filter(
                                    (item) => item.id !== row.id,
                                ),
                            });

                            onSuccess();
                        }}
                    />
                ),
            }),
        ],
    });

    return (
        <>
            <Outlet />
            <TableToolbar
                actions={
                    <Button.Add.Link
                        variant="secondary"
                        label="New third party contact type"
                        to="/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/third-party-contact-type/add"
                        params={{ orgId, productTypeId }}
                    />
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/third-party-contact-type/edit/$thirdPartyContactTypeId',
                                params: { orgId, productTypeId, thirdPartyContactTypeId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
